// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { z } from 'zod';

// This enum represents the shipping carriers that are supported by the Shippo

// This enum represents the shipping carriers that are supported by the Shippo
// API plus an addition 'other' value that represents a user-defined carrier.
//
// https://docs.goshippo.com/
export const shippingCarrierSchema = z.enum([
  'airterra',
  'apc_postal',
  'apg',
  'aramex',
  'asendia_us',
  'australia_post',
  'axlehire',
  'better_trucks',
  'borderguru',
  'boxberry',
  'bring',
  'canada_post',
  'cdl',
  'chronopost',
  'collect_plus',
  'correios_br',
  'correos_espana',
  'couriersplease',
  'colissimo',
  'deutsche_post',
  'dhl_benelux',
  'dhl_ecommerce',
  'dhl_express',
  'dhl_germany_c2c',
  'dhl_germany',
  'dpd_de',
  'dpd_uk',
  'estafeta',
  'fastway_australia',
  'fedex',
  'globegistics',
  'gls_us',
  'gophr',
  'gso',
  'hermes_germany_b2c',
  'hermes_uk',
  'hongkong_post',
  'la_poste',
  'lasership',
  'lso',
  'mondial_relay',
  'new_zealand_post',
  'nippon_express',
  'ontrac',
  'orangeds',
  'parcelforce',
  'parcel',
  'passport',
  'pcf',
  'pitney_bowes',
  'poste_italiane',
  'posti',
  'purolator',
  'royal_mail',
  'royal_mail_sf',
  'rr_donnelley',
  'russian_post',
  'sendle',
  'skypostal',
  'stuart',
  'swyft',
  'uds',
  'ups',
  'usps',
  'veho',
  'x_delivery',
  'yodel',

  // This value is not supported by the Shippo API but is used internally to
  // represent a user-defined carrier.
  'other',
]);

export type ShippingCarrier = z.infer<typeof shippingCarrierSchema>;

export const SHIPPING_CARRIERS = shippingCarrierSchema.options;

/** Schema for the carrier and tracking number of a shipment or movement. */
export const trackingNumberDetailsSchema = z.object({
  carrier: shippingCarrierSchema.nullable(),
  name: z.string().nullable(),
  trackingNumber: z.string().nullable(),
});

/** Schema for the carrier and transport reference of a purchase or return order. */
export const transportReferenceDetailsSchema = z.object({
  carrier: shippingCarrierSchema.nullable(),
  name: z.string().nullable(),
  transportReference: z.string().nullable(),
});

type TrackingNumberDetails = z.infer<typeof trackingNumberDetailsSchema>;

type TransportReferenceDetails = z.infer<
  typeof transportReferenceDetailsSchema
>;

/** Trims a typed value, treating a blank one as not given. */
function toTrimmedText(value: string | null): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed === '' ? null : trimmed;
}

/** Builds the tracking number details a shipment request sends. */
export function toTrackingNumberDetailsRequest(
  carrier: ShippingCarrier | null,
  name: string | null,
  trackingNumber: string | null
): TrackingNumberDetails | null {
  const trimmedTrackingNumber = toTrimmedText(trackingNumber);
  if (carrier === null && trimmedTrackingNumber === null) {
    return null;
  }

  return {
    carrier,
    name: carrier === 'other' ? toTrimmedText(name) : null,
    trackingNumber: trimmedTrackingNumber,
  };
}

/** Builds the transport reference details an order request sends. */
export function toTransportReferenceDetailsRequest(
  carrier: ShippingCarrier | null,
  name: string | null,
  transportReference: string | null
): TransportReferenceDetails | null {
  const trimmedTransportReference = toTrimmedText(transportReference);
  if (carrier === null && trimmedTransportReference === null) {
    return null;
  }

  return {
    carrier,
    name: carrier === 'other' ? toTrimmedText(name) : null,
    transportReference: trimmedTransportReference,
  };
}

const PRIMARY_SHIPPING_CARRIERS: ShippingCarrier[] = [
  'dhl_ecommerce',
  'dhl_express',
  'fedex',
  'ups',
  'usps',
];

const SHIPPING_CARRIER_LABELS: Record<ShippingCarrier, string> = {
  airterra: 'Airterra',
  apc_postal: 'APC Postal',
  apg: 'APG',
  aramex: 'Aramex',
  asendia_us: 'Asendia US',
  australia_post: 'Australia Post (also used for Startrack)',
  axlehire: 'Jitsu',
  better_trucks: 'BetterTrucks',
  borderguru: 'BorderGuru',
  boxberry: 'Boxberry',
  bring: 'Bring (also used for Posten Norge)',
  canada_post: 'Canada Post',
  cdl: 'CDL',
  chronopost: 'Chronopost',
  collect_plus: 'CollectPlus',
  correios_br: 'CorreiosBR',
  correos_espana: 'Correos España',
  couriersplease: 'Couriers Please',
  colissimo: 'Colissimo',
  deutsche_post: 'Deutsche Post',
  dhl_benelux: 'DHL Benelux',
  dhl_ecommerce: 'DHL eCommerce',
  dhl_express: 'DHL Express',
  dhl_germany_c2c: 'DHL Germany C2C',
  dhl_germany: 'DHL Germany',
  dpd_de: 'DPD GERMANY',
  dpd_uk: 'DPD UK',
  estafeta: 'Estafeta',
  fastway_australia: 'Aramex (formerly Fastway)',
  fedex: 'FedEx',
  globegistics: 'Globegistics (now Asendia)',
  gls_us: 'GLS US',
  gophr: 'Gophr',
  gso: 'GSO',
  hermes_germany_b2c: 'Hermes Germany B2C',
  hermes_uk: 'Evri UK',
  hongkong_post: 'Hongkong Post',
  la_poste: 'La Poste',
  lasership: 'LaserShip',
  lso: 'LSO',
  mondial_relay: 'Mondial Relay',
  new_zealand_post: 'New Zealand Post (also used for Pace and CourierPost)',
  nippon_express: 'Nippon Express',
  ontrac: 'OnTrac',
  orangeds: 'OrangeDS',
  other: 'Other',
  parcelforce: 'Parcelforce',
  parcel: 'Parcel',
  passport: 'Passport',
  pcf: 'PCF',
  pitney_bowes: 'Pitney Bowes',
  poste_italiane: 'Poste Italiane',
  posti: 'Posti',
  purolator: 'Purolator',
  royal_mail: 'Royal Mail',
  royal_mail_sf: 'Royal Mail Storefeeder',
  rr_donnelley: 'ePost Global',
  russian_post: 'Russian Post',
  sendle: 'Sendle',
  skypostal: 'SkyPostal',
  stuart: 'Stuart',
  swyft: 'Swyft',
  uds: 'UDS (United Delivery Service)',
  ups: 'UPS',
  usps: 'United States Postal Service (USPS)',
  veho: 'Veho',
  x_delivery: 'X Delivery (now Maergo)',
  yodel: 'Yodel',
};

/**
 * A convenience function that returns the label for a shipping carrier.
 *
 * @param carrier - The shipping carrier for which to get the label.
 */
export function getShippingCarrierLabel(carrier: ShippingCarrier) {
  return SHIPPING_CARRIER_LABELS[carrier];
}

/**
 * A convenience function that checks if a shipping carrier is considered a
 * "primary" carrier. Primary carriers are those that are commonly used and
 * are typically displayed more prominently in the UI.
 *
 * @param carrier - The shipping carrier to check.
 */
export function isPrimaryShippingCarrier(carrier: ShippingCarrier) {
  return PRIMARY_SHIPPING_CARRIERS.includes(carrier);
}

/**
 * Compares two shipping carriers by first checking if either are a primary
 * carrier, and then by comparing the labels of the carriers. Primary carriers
 * are sorted before non-primary carriers.
 *
 * @param carrier - The first shipping carrier to compare.
 * @param other - The second shipping carrier to compare.
 */
export function compareShippingCarriers(
  carrier: ShippingCarrier,
  other: ShippingCarrier
) {
  // If both or neither carriers are primary carriers, sort by their labels.
  if (isPrimaryShippingCarrier(carrier) === isPrimaryShippingCarrier(other)) {
    const carrierLabel = getShippingCarrierLabel(carrier);
    const otherLabel = getShippingCarrierLabel(other);

    return carrierLabel.localeCompare(otherLabel);
  }

  return isPrimaryShippingCarrier(carrier) ? -1 : 1;
}

/**
 * A type guard that checks if a value is a valid shipping carrier.
 *
 * @param carrier - The value to check.
 */
export function isShippingCarrier(
  carrier: unknown
): carrier is ShippingCarrier {
  const result = shippingCarrierSchema.safeParse(carrier);

  return result.success;
}

/**
 * Returns a tracking link for a shipping carrier and tracking identifier for
 * supported carriers, or null for unsupported carriers.
 *
 * @param carrier - A shipping carrier identifier.
 * @param identifier - A tracking identifier.
 */
export function getTrackingURLForShippingCarrier(
  carrier: ShippingCarrier,
  identifier: string
) {
  if (identifier == '') {
    return null;
  }

  if (carrier === 'ups') {
    return `https://www.ups.com/track?loc=en_US&tracknum=${identifier}`;
  }

  if (carrier === 'usps') {
    return `https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=${identifier}`;
  }

  if (carrier === 'fedex') {
    return `https://www.fedex.com/wtrk/track/?tracknumbers=${identifier}`;
  }

  if (carrier === 'dhl_ecommerce') {
    return `https://webtrack.dhlglobalmail.com/orders?trackingNumber=${identifier}`;
  }

  if (carrier === 'dhl_express') {
    // DHL in its infinite wisdom has a messed up SSL cert so we have to redirect to non-secure.
    return `http://www.dhl-usa.com/content/us/en/express/tracking.shtml?brand=DHL&AWB=${identifier}`;
  }

  return null;
}

interface ShippingCarrierLabelOptions {
  name?: string;
}

/**
 * Returns a tracking label for a shipping carrier and tracking identifier.
 *
 * @param carrier - A shipping carrier identifier.
 * @param identifier - A tracking identifier.
 * @param options.name - If provided, the name of the carrier.
 */
export function getTrackingLabelForShippingCarrier(
  carrier: ShippingCarrier | null,
  identifier: string | null,
  options?: ShippingCarrierLabelOptions
) {
  if (carrier !== null && identifier !== null) {
    const name = options?.name ?? getShippingCarrierLabel(carrier);

    return `${name} #${identifier}`;
  }

  if (carrier !== null) {
    return options?.name ?? getShippingCarrierLabel(carrier);
  }

  if (identifier !== null) {
    return `#${identifier}`;
  }

  return null;
}
